import { Link, useNavigate } from "react-router-dom";
import { Coins, Menu, X, User, Settings, CreditCard, HelpCircle, LogOut } from "lucide-react";
import { useState, useEffect } from "react";
import logo from "@/assets/logo.png";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/hooks/useSubscription";
import { useCreditStatistics } from "@/hooks/useCreditStatistics";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { ThemeToggle } from "@/components/ThemeToggle";

interface NavbarProps {
  transparentOnTop?: boolean;
}

export const Navbar = ({ transparentOnTop = false }: NavbarProps = {}) => {
  const { user } = useAuth();
  const { subscription, isLoading: subLoading } = useSubscription();
  const { currentBalance, isLoading: statsLoading } = useCreditStatistics(subscription);
  const balance = currentBalance ?? subscription?.credits_remaining ?? 0;
  const isLoading = subLoading || statsLoading;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();

  // Track scroll for navbar background
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  return (
    <>
      {/* Skip Navigation for Accessibility */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md"
      >
        Skip to main content
      </a>

      <nav 
        className={`sticky top-0 z-50 transition-all duration-300 border-b ${
          !transparentOnTop || isScrolled
            ? 'bg-background/80 backdrop-blur-xl border-border shadow-fluent-2' 
            : 'dark bg-transparent border-transparent text-foreground'
        }`}
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="container px-4 py-4 md:py-5">
          <div className="flex items-center justify-between">
            <Link 
              to="/" 
              className="flex items-center gap-2.5 hover:opacity-80 transition-opacity group"
            >
              <img 
                src={logo} 
                alt="Email2Contact Logo" 
                className="w-7 h-7 transition-transform group-hover:scale-105" 
              />
              <span className="text-base md:text-[17px] font-display font-bold tracking-tight">Email2Contact</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-4">
              <Link 
                to="/pricing" 
                className="text-sm font-medium hover:text-primary transition-colors"
              >
                Pricing
              </Link>
              {user && (
                <Link 
                  to="/support" 
                  className="text-sm font-medium hover:text-primary transition-colors"
                >
                  Support
                </Link>
              )}
              
              <ThemeToggle />
              
              {user ? (
                <div className="flex items-center gap-3">
                  {/* Credit Badge - clickable */}
                  <Link 
                    to="/settings?tab=credits"
                    className="app-pill app-pill-primary hover:bg-primary/15 transition-colors"
                    aria-label={`Current balance: ${balance} credits. Click to buy more.`}
                  >
                    <Coins className="w-3.5 h-3.5" aria-hidden="true" />
                    {isLoading ? (
                      <Skeleton className="h-3 w-6" />
                    ) : (
                      <span className="tabular-nums">{balance}</span>
                    )}
                  </Link>

                  {/* User Menu Dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="rounded-full"
                        aria-label="Open user menu"
                      >
                        <User className="w-5 h-5" aria-hidden="true" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuItem asChild>
                        <Link to="/settings" className="flex items-center gap-2 cursor-pointer">
                          <Settings className="w-4 h-4" />
                          Settings
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/pricing" className="flex items-center gap-2 cursor-pointer">
                          <CreditCard className="w-4 h-4" />
                          Plans & Pricing
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/support" className="flex items-center gap-2 cursor-pointer">
                          <HelpCircle className="w-4 h-4" />
                          Support & FAQ
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        onClick={handleSignOut}
                        className="text-destructive focus:text-destructive cursor-pointer"
                      >
                        <LogOut className="w-4 h-4 mr-2" />
                        Sign Out
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Button variant="ghost" asChild>
                    <Link to="/auth">Sign in</Link>
                  </Button>
                  <Button asChild className="shadow-fluent-2">
                    <a
                      href="https://marketplace.microsoft.com/en-us/product/WA200010021?tab=Overview"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Get Add-in
                    </a>
                  </Button>
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2 -mr-2 touch-target touch-active rounded-lg hover:bg-muted transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
              aria-expanded={isMobileMenuOpen}
            >
              <div className="relative w-6 h-6">
                <Menu className={`absolute inset-0 w-6 h-6 transition-all duration-300 ${isMobileMenuOpen ? 'opacity-0 rotate-90' : 'opacity-100 rotate-0'}`} />
                <X className={`absolute inset-0 w-6 h-6 transition-all duration-300 ${isMobileMenuOpen ? 'opacity-100 rotate-0' : 'opacity-0 -rotate-90'}`} />
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Menu Overlay & Slide-out */}
        <div 
          className={`fixed inset-0 bg-black/50 z-40 md:hidden transition-opacity duration-300 ${
            isMobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
          onClick={() => setIsMobileMenuOpen(false)}
          aria-hidden="true"
        />
        
        <div 
          className={`fixed top-[57px] right-0 w-72 h-[calc(100vh-57px)] bg-background shadow-lg z-50 md:hidden border-l border-border transform transition-transform duration-300 ease-out ${
            isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <nav className="flex flex-col p-4 space-y-2" role="menu">
            <Link 
              to="/pricing" 
              onClick={() => setIsMobileMenuOpen(false)}
              className="text-sm font-medium hover:text-primary transition-colors py-3 px-4 rounded-lg hover:bg-muted touch-active"
              role="menuitem"
            >
              Pricing
            </Link>
            {user && (
              <>
                <Link 
                  to="/support" 
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-sm font-medium hover:text-primary transition-colors py-3 px-4 rounded-lg hover:bg-muted touch-active"
                  role="menuitem"
                >
                  Support
                </Link>
                <Link 
                  to="/settings?tab=credits"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-2 px-4 py-3 bg-primary/10 hover:bg-primary/20 rounded-lg transition-colors touch-active"
                  role="menuitem"
                >
                  <Coins className="w-4 h-4 text-primary" />
                  {isLoading ? (
                    <Skeleton className="h-4 w-8" />
                  ) : (
                    <span className="text-sm font-medium">{balance} credits</span>
                  )}
                </Link>
                <Link 
                  to="/settings" 
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-2 py-3 px-4 text-sm font-medium hover:text-primary transition-colors rounded-lg hover:bg-muted touch-active"
                  role="menuitem"
                >
                  <Settings className="w-4 h-4" />
                  Settings
                </Link>
                <button
                  onClick={() => {
                    handleSignOut();
                    setIsMobileMenuOpen(false);
                  }}
                  className="flex items-center gap-2 py-3 px-4 text-sm font-medium text-destructive hover:text-destructive/80 transition-colors text-left rounded-lg hover:bg-destructive/10 touch-active"
                  role="menuitem"
                >
                  <LogOut className="w-4 h-4" />
                  Sign Out
                </button>
              </>
            )}
            {!user && (
              <Button 
                variant="outline"
                className="w-full mt-2 touch-target"
                asChild
              >
                <Link to="/auth" onClick={() => setIsMobileMenuOpen(false)}>
                  Sign In
                </Link>
              </Button>
            )}
          </nav>
        </div>
      </nav>
    </>
  );
};
